import { theme } from 'antd';
import { EnableIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const EnablePeopleModal = ({ children, visible: visibleProp, setVisible: setVisibleProp, personIDs = [] }) => {
	// ✅ Hooks
	const canEnablePerson = useManagePermittedRoles('enablePerson');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// ✅ Get all people from Redux
	const people = useSelector(selectPeople);

	// ✅ Get loading and success states from Redux
	const { loadingEnableProfile, enablePersonComplete } = useSelector((state) => state.People);

	// ✅ Memoized selected people to avoid unnecessary filtering
	const selectedPeople = useMemo(
		() => people.filter((person) => personIDs.includes(person.personID)),
		[people, personIDs]
	);

	// ✅ Memoized names of selected people
	const selectedNames = useMemo(
		() =>
			selectedPeople.map((person) =>
				person.firstName && person.lastName
					? `${person.firstName} ${person.lastName}`
					: person.displayName || person.email || '-'
			),
		[selectedPeople]
	);

	// ✅ Generate dynamic messages
	const getEnableTitle = () =>
		personIDs.length > 1
			? `Are you sure you want to enable these ${personIDs.length} profiles?`
			: 'Are you sure you want to enable this profile?';

	const getEnableButtonText = () => (personIDs.length > 1 ? 'Enable Profiles' : 'Enable');

	// ✅ Close modal function (memoized)
	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	// ✅ Handle Finish (Enable Person(s)) (memoized)
	const handleFinish = useCallback(() => {
		dispatch(peopleActions.enablePerson({ personIDs }));
	}, [dispatch, personIDs]);

	// ✅ Close modal when enable is successful
	useEffect(() => {
		if (enablePersonComplete) handleCancel();
	}, [enablePersonComplete, handleCancel]);

	return (
		<>
			<Tooltip
				title={!canEnablePerson ? permissionDeniedMessage : "Enable this Person's Profile"}
				placement='top'
			>
				<span onClick={(e) => canEnablePerson && setVisible(true)}>
					{children ? (
						children
					) : (
						<Button
							type='text'
							size='small'
							icon={<EnableIcon color='currentColor' width='16' height='16' />}
							disabled={loadingEnableProfile || !canEnablePerson}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loadingEnableProfile} block>
							Cancel
						</Button>
						<Button
							onClick={handleFinish}
							loading={loadingEnableProfile}
							disabled={loadingEnableProfile}
							type='primary'
							block
						>
							{getEnableButtonText()}
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<EnableIcon width={80} height={80} fillOpacity={1} />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								{getEnableTitle()}
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This action restores this person's <br />
								access to bookings.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected People</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, textAlign: 'center' }}>
								{selectedNames.join(', ')}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default EnablePeopleModal;
